import { useState } from "react";
import { toast } from "sonner";
import { FileText, X, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const FORMATS = ["docx", "pdf", "xlsx", "csv"];

// Deterministic document builder — user supplies structure; no model call, no image deps.
export function CreateDocDialog({ conversationId, onCreated, onClose }) {
  const [format, setFormat] = useState("docx");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const isSheet = format === "xlsx" || format === "csv";

  const submit = async () => {
    if (!title.trim()) { toast.error("A title is required."); return; }
    setBusy(true);
    try {
      let payload = { format, title: title.trim(), conversation_id: conversationId };
      if (isSheet) {
        const lines = body.split("\n").map((l) => l.trim()).filter(Boolean);
        if (lines.length === 0) { toast.error("Add at least one row (comma-separated)."); setBusy(false); return; }
        const rows = lines.map((l) => l.split(",").map((c) => c.trim()));
        payload.headers = rows[0];
        payload.rows = rows.slice(1);
      } else {
        const sections = body.split(/\n\s*\n/).map((chunk) => {
          const lines = chunk.split("\n");
          return { heading: lines[0]?.trim() || "", body: lines.slice(1).join("\n").trim() || lines[0]?.trim() || "" };
        }).filter((s) => s.heading || s.body);
        if (sections.length === 0) { toast.error("Add some content."); setBusy(false); return; }
        payload.sections = sections;
      }
      const { data } = await api.post("/tools/documents", payload);
      toast.success("Document created.");
      onCreated?.(data);
      onClose?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="create-doc-dialog">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative w-full sm:max-w-lg bg-wr-surface border border-wr-border p-5 sm:p-6 wr-fade-up max-h-[90dvh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <span className="wr-label !text-wr-red flex items-center gap-2"><FileText size={14} /> Create document</span>
          <button onClick={onClose} data-testid="create-doc-close" className="text-wr-sub hover:text-white"><X size={18} /></button>
        </div>
        <label className="wr-label">Format</label>
        <div className="flex gap-1.5 mt-1.5 mb-4 flex-wrap">
          {FORMATS.map((f) => (
            <button key={f} onClick={() => setFormat(f)} data-testid={`doc-format-${f}`}
              className={`wr-mono text-xs uppercase px-3 py-1.5 border ${format === f ? "border-wr-red bg-wr-redMuted text-white" : "border-wr-border text-wr-sub hover:text-white"}`}>{f}</button>
          ))}
        </div>
        <label className="wr-label">Title</label>
        <Input data-testid="doc-title" value={title} onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Q2 Operating Plan" className="mt-1.5 mb-4 bg-wr-bg border-wr-border rounded-none focus-visible:ring-0" />
        <label className="wr-label">{isSheet ? "Rows (first line = headers, comma-separated)" : "Content (blank line separates sections; first line of each = heading)"}</label>
        <Textarea data-testid="doc-body" value={body} onChange={(e) => setBody(e.target.value)} rows={8}
          placeholder={isSheet ? "Item,Amount\nRent,4000\nPayroll,22000" : "Summary\nWhat this plan covers...\n\nPriorities\n- Cut onboarding time\n- Close 3 deals"}
          className="mt-1.5 mb-4 bg-wr-bg border-wr-border rounded-none focus-visible:ring-0 resize-none wr-mono text-sm" />
        <Button onClick={submit} disabled={busy} data-testid="doc-generate"
          className="w-full rounded-none bg-wr-red hover:bg-wr-redHover text-white wr-mono uppercase tracking-wider h-11">
          {busy ? <><Loader2 size={15} className="mr-2 animate-spin" /> Generating…</> : "Generate document"}
        </Button>
      </div>
    </div>
  );
}
